import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { TimelineContainerModel } from '../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../utils/variables';
import { useCreateProjectTimeline } from '../components/timeline-list/hooks/use-create-project-timeline';
import { useDeleteProjectTimeline } from '../components/timeline-list/hooks/use-delete-project-timeline';
import { useUpdateProjectTimeline } from '../components/timeline-list/hooks/use-update-project-timeline';
import {
  OnCreateOptions,
  OnDeleteOptions,
  OnUpdateOptions,
} from '../components/timeline-list/types';
import { ProjectContext } from './project-state/project-state';

export type TimelineWithIndex = TimelineContainerModel & { index: number };

export interface ProjectTimelineContextValue {
  timelines: TimelineWithIndex[];
  isLoading: boolean;
  isRefetching: boolean;
  isMutating: boolean;
  refetch(this: void): Promise<void>;
  createTimeline(this: void, options: OnCreateOptions): Promise<void>;
  updateTimeline(
    this: void,
    payload: { container: TimelineWithIndex; options: OnUpdateOptions },
  ): Promise<void>;
  deleteTimeline(this: void, options: OnDeleteOptions): Promise<void>;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const ProjectTimelineContext = React.createContext<ProjectTimelineContextValue>(undefined!);

type Props = {
  children: React.ReactNode;
};

function sortTimelines(timelines: TimelineContainerModel[]): TimelineWithIndex[] {
  return timelines
    .slice()
    .sort((a, b) => {
      return (
        new Date(b.collectionDate).getTime() - new Date(a.collectionDate).getTime() ||
        b.id.localeCompare(a.id)
      );
    })
    .map((timeline, index, arr) => ({ ...timeline, index: arr.length - index }));
}

const ProjectTimelineProvider = ({ children }: Props) => {
  const { project } = React.useContext(ProjectContext);
  const projectId = project?.id;

  const timelinesQuery = useQuery({
    enabled: isDefined(projectId),
    queryKey: ['project-timelines', projectId],
    queryFn: async () => {
      if (!projectId) {
        return [] as TimelineWithIndex[];
      }

      const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
      const { data } = await projectService.getTimelineContainers({
        path: { projectId },
        query: {
          include: [
            'timelineContainer.pointCloud',
            'timelineContainer.orthoTile',
            'timelineContainer.gltftScan',
            'timelineContainer.tiledModel',
          ],
        },
      });

      return sortTimelines(Array.isArray(data) ? data : []);
    },
    networkMode: 'always',
    staleTime: 60_000,
    retry: false,
  });

  const createMutation = useCreateProjectTimeline({
    onSuccess: () => {
      void timelinesQuery.refetch();
    },
  });

  const updateMutation = useUpdateProjectTimeline({
    onSuccess: () => {
      void timelinesQuery.refetch();
    },
  });

  const deleteMutation = useDeleteProjectTimeline({
    onSuccess: () => {
      void timelinesQuery.refetch();
    },
  });

  const value: ProjectTimelineContextValue = {
    timelines: timelinesQuery.data ?? [],
    isLoading: timelinesQuery.isLoading,
    isRefetching: timelinesQuery.isRefetching,
    isMutating: createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
    refetch: async () => {
      await timelinesQuery.refetch();
    },
    createTimeline: async (options) => {
      if (!projectId) {
        return;
      }
      await createMutation.mutateAsync({
        path: { projectId },
        body: {
          date: options.dateTime.toISOString(),
          tiledModelId: options.tiledModelId,
          gltftScanId: options.gltftScanId,
          pointcloudId: options.pointCloudId,
          orthoTileId: options.orthoTileId,
          comment: options.comment,
        },
      });
    },
    updateTimeline: async (payload) => {
      const { container, options } = payload;
      const diffOrUndefined = (a: string | null, b: string | null): string | undefined | null =>
        a !== b ? (isDefined(b) ? b : null) : undefined;

      await updateMutation.mutateAsync({
        containerId: container.id,
        dateIsoString: options.dateTime?.toISOString(),
        tiledModelId: diffOrUndefined(container.tiledModelId ?? null, options.tiledModelId ?? null),
        scanId: diffOrUndefined(container.scanId ?? null, options.gltftScanId ?? null),
        pointCloudId: diffOrUndefined(container.pointCloudId ?? null, options.pointCloudId ?? null),
        orthoTileId: diffOrUndefined(container.orthoTileId ?? null, options.orthoTileId ?? null),
        comment: diffOrUndefined(container.comment ?? null, options.comment ?? null),
      });
    },
    deleteTimeline: async (options) => {
      await deleteMutation.mutateAsync({
        path: { containerId: options.timelineId },
      });
    },
  };

  return (
    <ProjectTimelineContext.Provider value={value}>{children}</ProjectTimelineContext.Provider>
  );
};

export { ProjectTimelineProvider };
